import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';

const initialForm = {
  email: '',
  role: 'developer',
  expires_in_days: 14,
};

const normalizeStatus = (value) => String(value || 'active').toLowerCase();
const normalizeRole = (value) => String(value || 'developer').trim().toLowerCase();
const normalizeToken = (value) => String(value || '').trim().toLowerCase();
const LEGACY_ROLE_VALUES = new Set(['user']);
const ROLE_OPTIONS = [
  { value: 'developer', label: 'Developer' },
  { value: 'qa', label: 'QA' },
  { value: 'lead', label: 'Lead' },
  { value: 'admin', label: 'Admin' },
];
const makeLabel = (value) => String(value || '')
  .replace(/[_:-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());
const normalizeRoleNameInput = (value) => normalizeToken(value).replace(/\s+/g, '_');
const ROLE_NAME_PATTERN = /^[a-z][a-z0-9_-]*$/;
const DEFAULT_PERMISSION_CATALOG = [
  'application:create',
  'application:view',
  'application:manage',
  'application:manage_members',
  'brd:view',
  'brd:upload',
  'brd:request_approval',
  'brd:approve',
  'brd:reject',
  'brd:share_request',
  'brd:share_approve',
  'brd:share_reject',
  'testcases:view',
  'testcases:generate',
  'testcases:edit',
  'testcases:export',
  'testcases:request_approval',
  'testcases:approve',
  'testcases:reject',
];
const PROVIDER_OPTIONS = [
  { value: 'claude', label: 'Claude' },
  { value: 'gemini', label: 'Gemini' },
];
const CLAUDE_MODEL_OPTIONS = [
  { value: 'claude-opus-5', label: 'Claude Opus 5 — highest quality, slowest/most expensive' },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5 — balanced' },
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5 — fastest, cheapest' },
];
const GEMINI_MODEL_OPTIONS = [
  { value: 'gemini-3.7-flash', label: 'Gemini 3.7 Flash — highest quality' },
  { value: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash' },
  { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite — faster, cheaper' },
  { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro — hard reasoning' },
  { value: 'gemini-omni-1.1-flash', label: 'Gemini Omni Flash — multimodal/live-style' },
];
const MODEL_OPTIONS_BY_PROVIDER = {
  claude: CLAUDE_MODEL_OPTIONS,
  gemini: GEMINI_MODEL_OPTIONS,
};
const MODEL_CATEGORY_OPTIONS = [
  { key: 'extraction', label: 'Extraction (vision, screenshots, BRD section grouping)' },
  { key: 'analysis', label: 'Analysis (test planning, gap analysis, BRD requirement delta)' },
  { key: 'test_case', label: 'Test case generation' },
  { key: 'test_script', label: 'Test script generation (Playwright / Appium, naming)' },
];
const normalizeCategorySetting = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const provider = PROVIDER_OPTIONS.some((option) => option.value === source.provider) ? source.provider : 'claude';
  const allowed = MODEL_OPTIONS_BY_PROVIDER[provider];
  const rawModel = String(source.model || '').trim();
  const model = allowed.some((option) => option.value === rawModel) ? rawModel : allowed[0].value;
  return { provider, model };
};
const normalizeModelSettings = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  return MODEL_CATEGORY_OPTIONS.reduce((acc, { key }) => {
    acc[key] = normalizeCategorySetting(source[key]);
    return acc;
  }, {});
};
const cloneModelSettings = (value) => normalizeModelSettings(JSON.parse(JSON.stringify(value || {})));

const makeRoleOptions = (roles) => {
  const sourceRoles = Array.isArray(roles) && roles.length
    ? roles
    : ROLE_OPTIONS.map((option) => option.value);
  return sourceRoles
    .map((role) => normalizeRole(role))
    .filter((role) => role && !LEGACY_ROLE_VALUES.has(role))
    .map((normalizedRole) => {
      const knownOption = ROLE_OPTIONS.find((option) => option.value === normalizedRole);
      return {
        value: normalizedRole,
        label: knownOption?.label || makeLabel(normalizedRole),
      };
    });
};

const uniqueTokens = (values) => Array.from(new Set(
  (Array.isArray(values) ? values : [])
    .map(normalizeToken)
    .filter(Boolean)
));

const sortPermissions = (permissions) => uniqueTokens(permissions).sort();

const normalizeStringMap = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value).reduce((acc, [key, mappedValue]) => {
    const normalizedKey = normalizeToken(key);
    const normalizedValue = normalizeToken(mappedValue);
    if (normalizedKey && normalizedValue) {
      acc[normalizedKey] = normalizedValue;
    }
    return acc;
  }, {});
};

const normalizeRbacConfig = (value) => {
  const fallbackGlobalRoles = ROLE_OPTIONS.map((option) => option.value);
  const configuredGlobalRoles = uniqueTokens(value?.global_roles).filter((role) => !LEGACY_ROLE_VALUES.has(role));
  const globalRoles = configuredGlobalRoles.length
    ? configuredGlobalRoles
    : fallbackGlobalRoles;
  const configuredInviteAllowedRoles = uniqueTokens(value?.invite_allowed_roles)
    .filter((role) => !LEGACY_ROLE_VALUES.has(role));
  const inviteAllowedRoles = configuredInviteAllowedRoles.length
    ? configuredInviteAllowedRoles
    : globalRoles;
  const requestedInviteDefaultRole = normalizeToken(value?.invite_default_role);
  const inviteDefaultRole =
    requestedInviteDefaultRole &&
    !LEGACY_ROLE_VALUES.has(requestedInviteDefaultRole) &&
    inviteAllowedRoles.includes(requestedInviteDefaultRole)
      ? requestedInviteDefaultRole
      : inviteAllowedRoles[0] || 'developer';
  const applicationRoles = uniqueTokens(value?.application_roles).length
    ? uniqueTokens(value?.application_roles)
    : ['developer', 'qa', 'lead', 'admin'];
  const rawMatrix = value?.application_permission_matrix || {};
  const applicationPermissionMatrix = applicationRoles.reduce((acc, role) => {
    acc[role] = sortPermissions(rawMatrix?.[role]);
    return acc;
  }, {});

  return {
    global_roles: globalRoles,
    admin_role: normalizeToken(value?.admin_role) || 'admin',
    invite_allowed_roles: inviteAllowedRoles,
    invite_default_role: inviteDefaultRole,
    application_roles: applicationRoles,
    application_default_role: normalizeToken(value?.application_default_role) || applicationRoles[0] || 'developer',
    application_owner_role: normalizeToken(value?.application_owner_role) || applicationRoles[0] || 'lead',
    application_role_aliases: normalizeStringMap(value?.application_role_aliases),
    application_role_from_global_role: normalizeStringMap(value?.application_role_from_global_role),
    application_reviewer_permission: normalizeToken(value?.application_reviewer_permission) || 'brd:approve',
    application_permission_matrix: applicationPermissionMatrix,
  };
};

const cloneRbacConfig = (value) => normalizeRbacConfig(JSON.parse(JSON.stringify(value || {})));

export const AdminInvitesPage = ({ currentUser }) => {
  const [form, setForm] = useState(initialForm);
  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [rbac, setRbac] = useState(null);
  const [rbacDraft, setRbacDraft] = useState(null);
  const [rbacLoading, setRbacLoading] = useState(false);
  const [rbacSaving, setRbacSaving] = useState(false);
  const [rbacEditing, setRbacEditing] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [modelSettings, setModelSettings] = useState(null);
  const [modelSettingsDraft, setModelSettingsDraft] = useState(null);
  const [modelSettingsLoading, setModelSettingsLoading] = useState(false);
  const [modelSettingsSaving, setModelSettingsSaving] = useState(false);
  const [modelSettingsEditing, setModelSettingsEditing] = useState(false);

  const inviteRoleOptions = useMemo(
    () => makeRoleOptions(rbacDraft?.invite_allowed_roles),
    [rbacDraft]
  );
  const userRoleOptions = useMemo(
    () => makeRoleOptions(rbacDraft?.global_roles),
    [rbacDraft]
  );
  const applicationRoleOptions = useMemo(
    () => makeRoleOptions(rbacDraft?.application_roles),
    [rbacDraft]
  );
  const permissionCatalog = useMemo(() => {
    if (!rbacDraft) return [];
    const permissions = new Set(DEFAULT_PERMISSION_CATALOG);
    Object.values(rbacDraft.application_permission_matrix || {}).forEach((rolePermissions) => {
      uniqueTokens(rolePermissions).forEach((permission) => permissions.add(permission));
    });
    if (rbacDraft.application_reviewer_permission) {
      permissions.add(normalizeToken(rbacDraft.application_reviewer_permission));
    }
    return sortPermissions(Array.from(permissions));
  }, [rbacDraft]);

  const loadInvites = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(API_ENDPOINTS.ADMIN_INVITES);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to load invites');
      }
      const data = await response.json();
      setInvites(Array.isArray(data.invites) ? data.invites : []);
    } catch (err) {
      setError(err.message || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setError('');
    try {
      const response = await apiFetch(API_ENDPOINTS.ADMIN_USERS);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to load users');
      }
      const data = await response.json();
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const loadRbac = useCallback(async () => {
    setRbacLoading(true);
    setError('');
    try {
      const response = await apiFetch(API_ENDPOINTS.ADMIN_RBAC);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to load access settings');
      }
      const data = await response.json();
      const normalizedRbac = normalizeRbacConfig(data.rbac);
      setRbac(normalizedRbac);
      setRbacDraft(cloneRbacConfig(normalizedRbac));
      setRbacEditing(false);
      setNewRoleName('');
      setForm((prev) => {
        const nextRole = normalizedRbac.invite_allowed_roles.includes(normalizeRole(prev.role))
          ? normalizeRole(prev.role)
          : normalizedRbac.invite_default_role;
        return { ...prev, role: nextRole };
      });
    } catch (err) {
      setError(err.message || 'Failed to load access settings');
    } finally {
      setRbacLoading(false);
    }
  }, []);

  const loadModelSettings = useCallback(async () => {
    setModelSettingsLoading(true);
    setError('');
    try {
      const response = await apiFetch(API_ENDPOINTS.ADMIN_MODEL_SETTINGS);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to load model settings');
      }
      const data = await response.json();
      const normalized = normalizeModelSettings(data.model_settings);
      setModelSettings(normalized);
      setModelSettingsDraft(cloneModelSettings(normalized));
      setModelSettingsEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to load model settings');
    } finally {
      setModelSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvites();
    loadUsers();
    loadRbac();
    loadModelSettings();
  }, [loadInvites, loadUsers, loadRbac, loadModelSettings]);

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!form.email.trim()) {
      setError('Email is required.');
      return;
    }

    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(API_ENDPOINTS.ADMIN_INVITES, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim().toLowerCase(),
          role: form.role,
          expires_in_days: Number(form.expires_in_days) || 14,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to create invite');
      }
      const data = await response.json();
      const normalizedEmail = form.email.trim().toLowerCase();
      const emailDelivery = data.email_delivery || {};
      let nextMessage = `Invite created for ${normalizedEmail}.`;
      if (emailDelivery.sent) {
        nextMessage += ' Invitation email sent.';
      } else if (emailDelivery.attempted && emailDelivery.detail) {
        nextMessage += ` Invite email not sent: ${emailDelivery.detail}`;
      }
      setMessage(nextMessage);
      setForm(initialForm);
      await loadInvites();
    } catch (err) {
      setError(err.message || 'Failed to create invite');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (email) => {
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(
        `${API_ENDPOINTS.ADMIN_INVITES}/${encodeURIComponent(email)}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to revoke invite');
      }
      setMessage(`Invite revoked for ${email}`);
      await loadInvites();
    } catch (err) {
      setError(err.message || 'Failed to revoke invite');
    }
  };

  const handleUserStatusChange = async (user, nextStatus) => {
    if (
      nextStatus === 'revoked' &&
      !window.confirm(`Revoke access for ${user.email}? This blocks sign-in until restored.`)
    ) {
      return;
    }

    setUpdatingUserId(user.id);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(`${API_ENDPOINTS.ADMIN_USERS}/${encodeURIComponent(user.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to update user access');
      }
      const data = await response.json();
      const emailDelivery = data.email_delivery || {};
      let nextMessage =
        nextStatus === 'revoked'
          ? `Access revoked for ${user.email}`
          : `Access restored for ${user.email}`;
      if (nextStatus === 'active' && emailDelivery.sent) {
        nextMessage += '. Restore email sent.';
      } else if (nextStatus === 'active' && emailDelivery.attempted && emailDelivery.detail) {
        nextMessage += ` Restore email not sent: ${emailDelivery.detail}`;
      }
      setMessage(nextMessage);
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to update user access');
    } finally {
      setUpdatingUserId('');
    }
  };

  const handleUserRoleChange = async (user, nextRole) => {
    const normalizedRole = normalizeRole(nextRole);
    if (normalizeRole(user.role) === normalizedRole) return;

    const isDowngradingAdmin = normalizeRole(user.role) === 'admin' && normalizedRole !== 'admin';
    if (
      isDowngradingAdmin &&
      !window.confirm(`Change ${user.email} from Admin to ${normalizedRole}?`)
    ) {
      return;
    }

    setUpdatingUserId(user.id);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(API_ENDPOINTS.ADMIN_USER_BY_ID(encodeURIComponent(user.id)), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: normalizedRole }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to update user role');
      }
      setMessage(`Role updated for ${user.email} to ${normalizedRole}.`);
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to update user role');
    } finally {
      setUpdatingUserId('');
    }
  };

  const handleRemoveUser = async (user) => {
    if (!window.confirm(`Remove ${user.email} permanently?`)) {
      return;
    }

    setUpdatingUserId(user.id);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(API_ENDPOINTS.ADMIN_USER_BY_ID(encodeURIComponent(user.id)), {
        method: 'DELETE',
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to remove user');
      }
      setMessage(`User removed: ${user.email}`);
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to remove user');
    } finally {
      setUpdatingUserId('');
    }
  };

  const updateRbacDraft = (updater) => {
    setRbacDraft((prev) => {
      if (!prev) return prev;
      const nextDraft = cloneRbacConfig(prev);
      updater(nextDraft);
      return normalizeRbacConfig(nextDraft);
    });
  };

  const handlePermissionToggle = (role, permission) => {
    if (!rbacEditing) return;
    const normalizedRole = normalizeRole(role);
    const normalizedPermission = normalizeToken(permission);
    if (!normalizedRole || !normalizedPermission) return;

    updateRbacDraft((draft) => {
      const permissions = new Set(draft.application_permission_matrix[normalizedRole] || []);
      if (permissions.has(normalizedPermission)) {
        permissions.delete(normalizedPermission);
      } else {
        permissions.add(normalizedPermission);
      }
      draft.application_permission_matrix[normalizedRole] = sortPermissions(Array.from(permissions));
    });
  };

  const handleAddRole = (event) => {
    event.preventDefault();
    if (!rbacEditing) return;

    const normalizedRole = normalizeRoleNameInput(newRoleName);
    if (!normalizedRole) {
      setError('Role name is required.');
      return;
    }
    if (!ROLE_NAME_PATTERN.test(normalizedRole)) {
      setError('Role name must start with a letter and use only letters, numbers, underscores, or hyphens.');
      return;
    }
    if (rbacDraft?.application_roles?.includes(normalizedRole)) {
      setError(`${normalizedRole} already exists.`);
      return;
    }

    updateRbacDraft((draft) => {
      draft.application_roles = uniqueTokens([...draft.application_roles, normalizedRole]);
      draft.global_roles = uniqueTokens([...draft.global_roles, normalizedRole]);
      draft.invite_allowed_roles = uniqueTokens([...draft.invite_allowed_roles, normalizedRole]);
      draft.application_role_from_global_role = {
        ...draft.application_role_from_global_role,
        [normalizedRole]: normalizedRole,
      };
      draft.application_permission_matrix[normalizedRole] = [];
    });
    setNewRoleName('');
    setError('');
    setMessage(`${normalizedRole} role added with no permissions. Save changes to persist it.`);
  };

  const handleStartRbacEdit = () => {
    if (!rbac) return;
    setRbacDraft(cloneRbacConfig(rbac));
    setNewRoleName('');
    setRbacEditing(true);
    setError('');
    setMessage('Access edit mode enabled. Save changes when you are done.');
  };

  const handleCancelRbacEdit = () => {
    if (rbac) {
      setRbacDraft(cloneRbacConfig(rbac));
    }
    setNewRoleName('');
    setRbacEditing(false);
    setError('');
    setMessage('Access edit mode cancelled. No changes were saved.');
  };

  const handleResetRbacDraft = () => {
    if (!rbac) return;
    setRbacDraft(cloneRbacConfig(rbac));
    setNewRoleName('');
    setError('');
    setMessage('Access changes reset to the last saved settings.');
  };

  const handleSaveRbac = async () => {
    if (!rbacDraft || !rbacEditing) return;

    setRbacSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = normalizeRbacConfig(rbacDraft);
      const response = await apiFetch(API_ENDPOINTS.ADMIN_RBAC, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.detail || 'Failed to save access settings');
      }
      const data = await response.json();
      const normalizedRbac = normalizeRbacConfig(data.rbac);
      setRbac(normalizedRbac);
      setRbacDraft(cloneRbacConfig(normalizedRbac));
      setRbacEditing(false);
      setNewRoleName('');
      setMessage('Access settings saved. New permission checks use the updated config.');
      await Promise.all([loadInvites(), loadUsers()]);
    } catch (err) {
      setError(err.message || 'Failed to save access settings');
    } finally {
      setRbacSaving(false);
    }
  };

  const handleStartModelSettingsEdit = () => {
    if (!modelSettings) return;
    setModelSettingsDraft(cloneModelSettings(modelSettings));
    setModelSettingsEditing(true);
  };

  const handleCancelModelSettingsEdit = () => {
    if (modelSettings) {
      setModelSettingsDraft(cloneModelSettings(modelSettings));
    }
    setModelSettingsEditing(false);
  };

  const handleResetModelSettingsDraft = () => {
    if (!modelSettings) return;
    setModelSettingsDraft(cloneModelSettings(modelSettings));
  };

  const handleCategoryProviderChange = (categoryKey, nextProvider) => {
    if (!modelSettingsEditing) return;
    setModelSettingsDraft((prev) => {
      const draft = prev || {};
      return {
        ...draft,
        [categoryKey]: normalizeCategorySetting({ provider: nextProvider }),
      };
    });
  };

  const handleCategoryModelChange = (categoryKey, nextModel) => {
    if (!modelSettingsEditing) return;
    setModelSettingsDraft((prev) => {
      const draft = prev || {};
      const current = draft[categoryKey] || {};
      return {
        ...draft,
        [categoryKey]: normalizeCategorySetting({ provider: current.provider, model: nextModel }),
      };
    });
  };

  const handleSaveModelSettings = async () => {
    if (!modelSettingsDraft || !modelSettingsEditing) return;

    setModelSettingsSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = normalizeModelSettings(modelSettingsDraft);
      const response = await apiFetch(API_ENDPOINTS.ADMIN_MODEL_SETTINGS, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.detail || 'Failed to save model settings');
      }
      const data = await response.json();
      const normalized = normalizeModelSettings(data.model_settings);
      setModelSettings(normalized);
      setModelSettingsDraft(cloneModelSettings(normalized));
      setModelSettingsEditing(false);
      setMessage('Model settings saved. New generation requests use the updated models immediately.');
    } catch (err) {
      setError(err.message || 'Failed to save model settings');
    } finally {
      setModelSettingsSaving(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
        <section className="rounded-3xl border border-white/80 bg-white/95 p-6 shadow-[0_16px_42px_rgba(15,23,42,0.08)]">
          <h1 className="text-2xl font-bold text-slate-900">Admin Portal</h1>
          <p className="mt-1 text-sm text-slate-600">
            Invite users, manage active access, and edit application permissions.
          </p>
          {error && (
            <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}
          {message && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {message}
            </div>
          )}
        </section>

        <section className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Invite User</h2>
            <form onSubmit={handleInvite} className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
                <input
                  className="text-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="user@company.com"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Role</label>
                <select
                  className="text-input"
                  value={form.role}
                  onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
                >
                  {inviteRoleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Expires In (days)</label>
                <input
                  className="text-input"
                  type="number"
                  min={1}
                  max={365}
                  value={form.expires_in_days}
                  onChange={(e) => setForm((prev) => ({ ...prev, expires_in_days: e.target.value }))}
                />
              </div>
              <button type="submit" className="primary-btn w-full" disabled={submitting}>
                {submitting ? 'Inviting...' : 'Send Invite'}
              </button>
            </form>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Invites</h2>
              <button className="secondary-btn px-3 py-1.5 text-sm" onClick={loadInvites} disabled={loading}>
                Refresh
              </button>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600">
                    <th className="px-2 py-2 font-semibold">Email</th>
                    <th className="px-2 py-2 font-semibold">Role</th>
                    <th className="px-2 py-2 font-semibold">Status</th>
                    <th className="px-2 py-2 font-semibold">Expires</th>
                    <th className="px-2 py-2 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((invite) => (
                    <tr key={invite.email} className="border-b border-slate-100">
                      <td className="px-2 py-2 text-slate-800">{invite.email}</td>
                      <td className="px-2 py-2 text-slate-700">{invite.role || 'developer'}</td>
                      <td className="px-2 py-2 text-slate-700">{invite.status || '-'}</td>
                      <td className="px-2 py-2 text-slate-700">
                        {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-2 py-2">
                        {invite.status === 'active' ? (
                          <button
                            className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                            onClick={() => handleRevoke(invite.email)}
                          >
                            Revoke
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!loading && invites.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-2 py-8 text-center text-slate-500">
                        No invites found.
                      </td>
                    </tr>
                  )}
                  {loading && (
                    <tr>
                      <td colSpan={5} className="px-2 py-8 text-center text-slate-500">
                        Loading invites...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Users & Access</h2>
              <p className="mt-1 text-sm text-slate-600">
                Revoked users lose API access and cannot sign in until restored.
              </p>
            </div>
            <button className="secondary-btn px-3 py-1.5 text-sm" onClick={loadUsers} disabled={usersLoading}>
              Refresh
            </button>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600">
                  <th className="px-2 py-2 font-semibold">Name</th>
                  <th className="px-2 py-2 font-semibold">Email</th>
                  <th className="px-2 py-2 font-semibold">Role</th>
                  <th className="px-2 py-2 font-semibold">Status</th>
                  <th className="px-2 py-2 font-semibold">Created</th>
                  <th className="px-2 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const userStatus = normalizeStatus(user.status);
                  const isCurrentUser = user.id === currentUser?.id;
                  const isBusy = updatingUserId === user.id;

                  return (
                    <tr key={user.id} className="border-b border-slate-100">
                      <td className="px-2 py-2 text-slate-800">
                        <div className="flex items-center gap-2">
                          <span>{user.name || '-'}</span>
                          {isCurrentUser && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                              You
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-slate-700">{user.email || '-'}</td>
                      <td className="px-2 py-2 text-slate-700">
                        {isCurrentUser ? (
                          <span className="text-xs text-slate-500">{normalizeRole(user.role)}</span>
                        ) : (
                          <select
                            className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs"
                            value={normalizeRole(user.role)}
                            onChange={(event) => handleUserRoleChange(user, event.target.value)}
                            disabled={isBusy}
                          >
                            {userRoleOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-2 py-2 text-slate-700">{userStatus}</td>
                      <td className="px-2 py-2 text-slate-700">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-2 py-2">
                        {isCurrentUser ? (
                          <span className="text-xs text-slate-400">Current session</span>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            {userStatus === 'revoked' ? (
                              <button
                                className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                                onClick={() => handleUserStatusChange(user, 'active')}
                                disabled={isBusy}
                              >
                                {isBusy ? 'Restoring...' : 'Restore'}
                              </button>
                            ) : (
                              <button
                                className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                                onClick={() => handleUserStatusChange(user, 'revoked')}
                                disabled={isBusy}
                              >
                                {isBusy ? 'Revoking...' : 'Revoke Access'}
                              </button>
                            )}
                            <button
                              className="rounded-lg border border-rose-300 bg-white px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                              onClick={() => handleRemoveUser(user)}
                              disabled={isBusy}
                            >
                              {isBusy ? 'Removing...' : 'Remove User'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!usersLoading && users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-2 py-8 text-center text-slate-500">
                      No users found.
                    </td>
                  </tr>
                )}
                {usersLoading && (
                  <tr>
                    <td colSpan={6} className="px-2 py-8 text-center text-slate-500">
                      Loading users...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Roles & Permissions</h2>
              <p className="mt-1 text-sm text-slate-600">
                View permissions by default. Click Edit Access before changing role permissions.
              </p>
              <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                rbacEditing
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-slate-100 text-slate-600'
              }`}>
                {rbacEditing ? 'Editing enabled' : 'Read-only'}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="secondary-btn px-3 py-1.5 text-sm"
                type="button"
                onClick={loadRbac}
                disabled={rbacLoading || rbacSaving}
              >
                {rbacLoading ? 'Refreshing...' : 'Refresh'}
              </button>
              {rbacEditing ? (
                <button
                  className="secondary-btn px-3 py-1.5 text-sm"
                  type="button"
                  onClick={handleCancelRbacEdit}
                  disabled={rbacSaving}
                >
                  Cancel Edit
                </button>
              ) : (
                <button
                  className="primary-btn px-3 py-1.5 text-sm"
                  type="button"
                  onClick={handleStartRbacEdit}
                  disabled={!rbac || rbacLoading || rbacSaving}
                >
                  Edit Access
                </button>
              )}
              <button
                className="secondary-btn px-3 py-1.5 text-sm"
                type="button"
                onClick={handleResetRbacDraft}
                disabled={!rbac || !rbacEditing || rbacSaving}
              >
                Reset Draft
              </button>
              <button
                className="primary-btn px-3 py-1.5 text-sm"
                type="button"
                onClick={handleSaveRbac}
                disabled={!rbacDraft || !rbacEditing || rbacSaving}
              >
                {rbacSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

          {rbacDraft ? (
            <>
              <form
                onSubmit={handleAddRole}
                className="mt-5 flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-end"
              >
                <div className="flex-1">
                  <label className="mb-1 block text-sm font-medium text-slate-700">New Role</label>
                  <input
                    className="text-input"
                    value={newRoleName}
                    onChange={(event) => setNewRoleName(event.target.value)}
                    placeholder="business_analyst"
                    disabled={!rbacEditing || rbacSaving}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    New roles start with no permissions checked.
                  </p>
                </div>
                <button
                  className="secondary-btn px-3 py-2 text-sm"
                  type="submit"
                  disabled={!rbacEditing || rbacSaving || !newRoleName.trim()}
                >
                  Add Role
                </button>
              </form>

              <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-slate-600">
                      <th className="sticky left-0 z-10 bg-slate-50 px-3 py-2 font-semibold">Permission</th>
                      {applicationRoleOptions.map((option) => (
                        <th key={option.value} className="px-3 py-2 text-center font-semibold">
                          {option.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {permissionCatalog.map((permission) => (
                      <tr key={permission} className="border-b border-slate-100 last:border-0">
                        <td className="sticky left-0 z-10 bg-white px-3 py-2 font-mono text-xs text-slate-800">
                          {permission}
                        </td>
                        {applicationRoleOptions.map((option) => {
                          const checked = Boolean(
                            rbacDraft.application_permission_matrix?.[option.value]?.includes(permission)
                          );
                          return (
                            <td key={`${permission}-${option.value}`} className="px-3 py-2 text-center">
                              <input
                                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                type="checkbox"
                                checked={checked}
                                onChange={() => handlePermissionToggle(option.value, permission)}
                                disabled={!rbacEditing || rbacSaving}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {permissionCatalog.length === 0 && (
                      <tr>
                        <td
                          colSpan={applicationRoleOptions.length + 1}
                          className="px-3 py-8 text-center text-slate-500"
                        >
                          No permissions configured.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-8 text-center text-sm text-slate-500">
              {rbacLoading ? 'Loading access settings...' : 'Access settings are unavailable.'}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Model Settings</h2>
              <p className="mt-1 text-sm text-slate-600">
                Choose which Claude model each generation stage uses. Takes effect on the next request — no restart needed.
              </p>
              <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                modelSettingsEditing
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-slate-100 text-slate-600'
              }`}>
                {modelSettingsEditing ? 'Editing enabled' : 'Read-only'}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="secondary-btn px-3 py-1.5 text-sm"
                type="button"
                onClick={loadModelSettings}
                disabled={modelSettingsLoading || modelSettingsSaving}
              >
                {modelSettingsLoading ? 'Refreshing...' : 'Refresh'}
              </button>
              {modelSettingsEditing ? (
                <button
                  className="secondary-btn px-3 py-1.5 text-sm"
                  type="button"
                  onClick={handleCancelModelSettingsEdit}
                  disabled={modelSettingsSaving}
                >
                  Cancel Edit
                </button>
              ) : (
                <button
                  className="primary-btn px-3 py-1.5 text-sm"
                  type="button"
                  onClick={handleStartModelSettingsEdit}
                  disabled={!modelSettings || modelSettingsLoading || modelSettingsSaving}
                >
                  Edit Models
                </button>
              )}
              <button
                className="secondary-btn px-3 py-1.5 text-sm"
                type="button"
                onClick={handleResetModelSettingsDraft}
                disabled={!modelSettings || !modelSettingsEditing || modelSettingsSaving}
              >
                Reset Draft
              </button>
              <button
                className="primary-btn px-3 py-1.5 text-sm"
                type="button"
                onClick={handleSaveModelSettings}
                disabled={!modelSettingsDraft || !modelSettingsEditing || modelSettingsSaving}
              >
                {modelSettingsSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

          {modelSettingsDraft ? (
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {MODEL_CATEGORY_OPTIONS.map(({ key, label }) => {
                const categorySetting = modelSettingsDraft[key] || { provider: 'claude', model: '' };
                return (
                  <div key={key} className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3">
                    <span className="text-sm font-medium text-slate-700">{label}</span>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-slate-500">Provider</span>
                      <select
                        className="text-input"
                        value={categorySetting.provider}
                        disabled={!modelSettingsEditing || modelSettingsSaving}
                        onChange={(event) => handleCategoryProviderChange(key, event.target.value)}
                      >
                        {PROVIDER_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-slate-500">Model</span>
                      <select
                        className="text-input"
                        value={categorySetting.model}
                        disabled={!modelSettingsEditing || modelSettingsSaving}
                        onChange={(event) => handleCategoryModelChange(key, event.target.value)}
                      >
                        {MODEL_OPTIONS_BY_PROVIDER[categorySetting.provider].map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-8 text-center text-sm text-slate-500">
              {modelSettingsLoading ? 'Loading model settings...' : 'Model settings are unavailable.'}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
